/**
 * «Утвердить справочник» — карточка секции бюджетного справочника.
 *
 * UAT founder 20.08 (прод v1.1746): «а как справочники утверждать?» — хаб
 * считал «N из 6», рычага утверждения не было ни на одном экране. Бэк уже
 * нёс полный жизненный цикл (`lib/planning/seasonCatalogApproval.ts`
 * объясняет откуда) — здесь только кнопка, бейдж статуса и подтверждение
 * снятия.
 *
 * Правка позиции в утверждённом справочнике снимает утверждение
 * АВТОМАТИЧЕСКИ на бэке (`season_planning.mark_catalog_in_progress`) — без
 * отдельного запроса с этого экрана. Предупреждение здесь статичное
 * (показывается, пока справочник утверждён), тост при фактическом снятии —
 * в `budget-catalogs/index.tsx` (единственное место, которое видит переход
 * `approved -> revised` из ответа сервера).
 */
import { useState } from 'react';
import { ShieldCheck, Undo2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { formatRuDate } from '@/lib/dateFmt';
import {
  useApproveCatalog,
  useUnapproveCatalog,
  type SeasonCatalog,
} from '@/lib/planning/seasonCatalogApproval';

function StatusBadge({ catalog }: { catalog: SeasonCatalog }) {
  if (catalog.status === 'approved') {
    const who = catalog.approved_by_name ? ` · ${catalog.approved_by_name}` : '';
    return (
      <Badge variant="primary" title={`Утверждён ${formatRuDate(catalog.approved_at)}${who}`}>
        Утверждён · {formatRuDate(catalog.approved_at)}
        {who}
      </Badge>
    );
  }
  if (catalog.status === 'revised') {
    return (
      <Badge variant="accent-brand" title="Был утверждён, но позиции поменялись — нужно утвердить заново">
        Пересмотрен
      </Badge>
    );
  }
  if (catalog.status === 'in_progress') {
    return <Badge variant="outline">В работе</Badge>;
  }
  return <Badge variant="outline">Не заполнен</Badge>;
}

export function CatalogApprovalBar({
  seasonId,
  catalogKind,
  farmId,
  catalog,
  canApprove,
  isLoading,
  isEmpty,
}: {
  seasonId: string;
  catalogKind: string;
  farmId: string | null;
  catalog: SeasonCatalog | undefined;
  /** Право `season_catalog_approval:U` — ОРТОГОНАЛЬНО праву редактировать позиции. */
  canApprove: boolean;
  /**
   * Статус ещё не приехал (`useSeasonCatalogs` пока грузится). Пока не
   * знаем реальный статус — кнопка/бейдж НЕ рисуются вовсе: до фикса здесь
   * на миг мигало «Утвердить справочник» даже для УЖЕ утверждённого
   * справочника (`catalog === undefined` до первого ответа читалось как
   * «не утверждён»), и клик по ложной кнопке дублировал бы утверждение
   * (head-web ревью #850).
   */
  isLoading: boolean;
  /** В справочнике (или во всех его подвкладках, для ТМЦ) нет позиций. */
  isEmpty: boolean;
}) {
  const approve = useApproveCatalog(seasonId);
  const unapprove = useUnapproveCatalog(seasonId);
  const [unapproveConfirmOpen, setUnapproveConfirmOpen] = useState(false);
  const [approveEmptyConfirmOpen, setApproveEmptyConfirmOpen] = useState(false);

  if (isLoading) {
    return <Skeleton className="h-8 w-64" />;
  }

  const approved = catalog?.status === 'approved';

  return (
    <div className="flex flex-wrap items-center gap-2">
      {catalog && <StatusBadge catalog={catalog} />}

      {/* Без права на утверждение — только бейдж, без кнопок (гейт входит в
          терминальное действие, как требует раздел «Массовые действия»
          CLAUDE.md: право проверяется ДО, не после клика). */}
      {canApprove && !approved && (
        <Button
          size="sm"
          onClick={() => {
            // Пустой справочник технически можно утвердить (версия — просто
            // счётчик, не гарантия наполненности), но founder должен решить
            // это осознанно, а не кликнуть по инерции (head-web ревью #850).
            if (isEmpty) {
              setApproveEmptyConfirmOpen(true);
              return;
            }
            approve.mutate({ catalogKind, farmId });
          }}
          disabled={approve.isPending}
        >
          <ShieldCheck className="mr-2 h-4 w-4" />
          {approve.isPending ? 'Утверждаем…' : 'Утвердить справочник'}
        </Button>
      )}

      {canApprove && approved && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => setUnapproveConfirmOpen(true)}
          disabled={unapprove.isPending}
        >
          <Undo2 className="mr-2 h-4 w-4" />
          Снять утверждение
        </Button>
      )}

      <Dialog open={unapproveConfirmOpen} onOpenChange={setUnapproveConfirmOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Снять утверждение справочника?</DialogTitle>
            <DialogDescription>
              Справочник вернётся в статус «В работе». Утверждён {formatRuDate(catalog?.approved_at)}
              {catalog?.approved_by_name ? ` · ${catalog.approved_by_name}` : ''} — версия при этом не
              теряется, позиции остаются как есть.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setUnapproveConfirmOpen(false)}
              disabled={unapprove.isPending}
            >
              Отмена
            </Button>
            <Button
              onClick={() =>
                unapprove.mutate(
                  { catalogKind, farmId },
                  { onSuccess: () => setUnapproveConfirmOpen(false) },
                )
              }
              disabled={unapprove.isPending}
            >
              {unapprove.isPending ? 'Снимаем…' : 'Снять утверждение'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={approveEmptyConfirmOpen} onOpenChange={setApproveEmptyConfirmOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Утвердить пустой справочник?</DialogTitle>
            <DialogDescription>
              В справочнике нет позиций. Их можно добавить и после утверждения — правка
              автоматически снимет утверждение, заново утвердить можно будет сразу.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setApproveEmptyConfirmOpen(false)}
              disabled={approve.isPending}
            >
              Отмена
            </Button>
            <Button
              onClick={() =>
                approve.mutate(
                  { catalogKind, farmId },
                  { onSuccess: () => setApproveEmptyConfirmOpen(false) },
                )
              }
              disabled={approve.isPending}
            >
              {approve.isPending ? 'Утверждаем…' : 'Всё равно утвердить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Предупреждение под шапкой секции — статично, пока справочник утверждён. */
export function ApprovedEditWarning({ catalog }: { catalog: SeasonCatalog | undefined }) {
  if (catalog?.status !== 'approved') return null;
  return (
    <p className="rounded-md border border-accent-brand/40 bg-accent-brand/10 px-3 py-2 text-sm text-accent-brand">
      Справочник утверждён — правка любой позиции автоматически снимет утверждение (статус
      «Пересмотрен»), заново утвердить можно будет сразу.
    </p>
  );
}
